import type { Messages } from './index'

/**
 * Bahasa Malaysia. Register is deliberately everyday spoken BM, not formal
 * written BM — the audience is part-time crew, not a government form.
 * Common English loanwords (shift, tier, QR) are kept because that is what
 * people actually say.
 */
export const ms: Messages = {
  common: {
    back: 'Kembali', next: 'Seterusnya', cancel: 'Batal', confirm: 'Sahkan', close: 'Tutup',
    save: 'Simpan', done: 'Selesai', retry: 'Cuba lagi', loading: 'Memuatkan',
    today: 'Hari ini', tomorrow: 'Esok', hours: 'jam', perHour: '/jam',
    somethingWrong: 'Ada masalah berlaku.',
  },
  nav: { shifts: 'Shift', myShifts: 'Shift saya', learn: 'Belajar', earnings: 'Pendapatan', profile: 'Profil' },
  auth: {
    title: 'Kerja bila anda nak',
    subtitle: 'Shift separuh masa yang disahkan dari majikan seluruh Malaysia. Satu profil, banyak tempat kerja.',
    phoneLabel: 'Nombor telefon',
    phoneHint: 'Kami akan SMS kod 6 digit kepada anda',
    sendCode: 'Hantar kod',
    codeTitle: 'Masukkan kod anda',
    codeSentTo: 'Kami hantar kod 6 digit ke {phone}',
    codeLabel: 'Kod 6 digit',
    verify: 'Sahkan',
    resend: 'Hantar semula',
    changeNumber: 'Tukar nombor',
    demoHint: 'Mod demo — kodnya 123456',
    invalidPhone: 'Masukkan nombor telefon Malaysia yang sah.',
  },
  vetting: {
    title: 'Menyiapkan anda',
    subtitle: 'Majikan pilih Pinch Crew sebab semua orang di sini disemak. Beberapa langkah lagi, anda boleh mula ambil shift.',
    progress: 'Proses semakan',
    stageStatus: {
      not_started: 'Belum mula', submitted: 'Dihantar', in_review: 'Sedang disemak',
      passed: 'Selesai', failed: 'Perlu tindakan',
    },
    basicsTitle: 'Apa nama anda?',
    basicsBody: 'Nama seperti dalam MyKad anda. Majikan akan lihat nama ini apabila anda ambil syif.',
    basicsName: 'Nama penuh',
    basicsNamePlaceholder: 'cth. Nur Aisyah binti Rahman',
    basicsEmail: 'E-mel (pilihan)',
    basicsEmailHelp: 'Cara kedua untuk log masuk dan terima resit.',
    basicsPostcode: 'Kod pos anda',
    basicsPostcodeHelp: 'Kami guna ini untuk tunjuk shift berdekatan rumah anda. Lima digit.',
    basicsPostcodeFound: 'Berdekatan {area}',
    basicsPostcodeUnknown: 'Kami tidak kenal kod pos itu. Semak lima digit tersebut.',
    basicsSave: 'Simpan dan teruskan',
    bankTitle: 'Ke mana kami patut bayar?',
    bankBody: 'Kami bayar ke akaun ini selepas setiap syif disahkan. Ops akan menyemaknya sekali sebelum bayaran pertama anda.',
    bankName: 'Bank',
    bankAccountNo: 'Nombor akaun',
    bankHolder: 'Nama pemegang akaun',
    bankHolderHelp: 'Mesti sama tepat dengan bank, jika tidak pindahan akan gagal.',
    bankSave: 'Simpan akaun',
    bankAdd: 'Tambah akaun bayaran',
    bankEdit: 'Tukar akaun',
    bankVerified: 'Disahkan',
    bankPending: 'Kami sedang menyemak akaun ini',
    bankMissing: 'Tambah akaun bayaran supaya kami boleh bayar anda.',
    submitted: 'Dihantar untuk semakan',
    submittedBody: 'Dokumen anda telah kami terima. Penyemak akan memeriksanya dalam masa 48 jam dan kami akan maklumkan sebarang perubahan.',
    aboutYou: 'Tentang anda',
    aboutYouBody: 'Beberapa soalan supaya kami boleh padankan anda dengan shift yang sesuai. Ambil masa dua minit.',
    unlocks: 'Membuka {what}',
    required: 'Wajib',
    optional: 'Pilihan',
    documents: 'Dokumen anda',
    upload: 'Muat naik',
    replace: 'Ganti',
    docStatus: { pending: 'Sedang disemak', verified: 'Disahkan', rejected: 'Ditolak' },
    docType: {
      ic: 'MyKad (IC)', bank: 'Akaun bank', student_pass: 'Pas pelajar',
      work_permit: 'Permit kerja', food_handling_cert: 'Sijil pengendali makanan',
      typhoid_jab: 'Suntikan tifoid',
    },
    allDone: 'Anda telah disahkan',
    allDoneBody: 'Anda boleh ambil mana-mana shift yang anda layak.',
  },
  shifts: {
    title: 'Shift untuk anda',
    subtitle: 'Dalam {km} km dari kawasan rumah anda',
    subtitleNoHome: 'Menunjukkan shift di semua tempat · tetapkan kawasan anda untuk tapis ikut jarak',
    setHome: 'Tetapkan kawasan anda',
    empty: 'Tiada shift kosong buat masa ini',
    emptyNotActive: 'Selesaikan pemeriksaan anda untuk mula mengambil syif',
    emptyNotActiveBody: '{n} syif sedang menunggu. Anda boleh mengambilnya sebaik sahaja pengesahan anda diluluskan. | {n} syif sedang menunggu. Anda boleh mengambilnya sebaik sahaja pengesahan anda diluluskan.',
    emptyNoSkills: 'Tambah kemahiran untuk melihat syif yang sepadan',
    emptyNoSkillsBody: 'Syif dipadankan dengan kemahiran anda. Selesaikan kursus pendek untuk membuka kemahiran pertama anda.',
    emptyTierTooLow: 'Syif ini memerlukan tahap yang lebih tinggi',
    emptyTierTooLowBody: '{n} syif terbuka dalam bidang anda, tetapi pada tahap lebih tinggi. Selesaikan kursus untuk naik tahap. | {n} syif terbuka dalam bidang anda, tetapi pada tahap lebih tinggi. Selesaikan kursus untuk naik tahap.',
    emptyOtherSkills: 'Tiada syif dalam kemahiran anda sekarang',
    emptyOtherSkillsBody: '{n} syif terbuka dalam kemahiran lain. Pelajarinya untuk meluaskan peluang anda. | {n} syif terbuka dalam kemahiran lain. Pelajari satu untuk meluaskan peluang anda.',
    emptyBody: 'Kami akan beritahu sebaik sahaja ada tempat kerja berdekatan perlukan orang.',
    emptyAction: 'Tetapkan masa lapang saya',
    funded: 'Dijamin',
    fundedExplain: 'Majikan sudah bayar. Gaji anda terjamin sebelum anda mula.',
    startingSoon: 'Bermula tidak lama lagi',
    spotsLeft: 'Tinggal {n} kekosongan | Tinggal {n} kekosongan',
    claim: 'Saya ambil',
    confirmTitle: 'Ambil syif ini?',
    confirmBody: 'Anda berkomitmen untuk bekerja di {site} pada {when}. Tidak hadir akan menjejaskan skor kebolehpercayaan anda, jadi ambil hanya jika anda boleh hadir.',
    confirmCta: 'Ya, saya akan hadir',
    confirmBack: 'Belum lagi',
    confirmPay: 'Anda akan dapat',
    claiming: 'Sedang ambil…',
    claimed: 'Shift disahkan',
    claimedBody: 'Jumpa di {site}. Kami dah masukkan dalam senarai shift anda.',
    details: 'Butiran shift',
    dressCode: 'Pakaian',
    instructions: 'Tempat lapor diri',
    breakPaid: 'Rehat berbayar {n} minit',
    estimatedPay: 'Anggaran bayaran',
    payExplain: '{rate}/jam × {hours} jam',
    tierRequired: 'Perlu Tier {n}',
    filters: 'Tapisan',
    filterAll: 'Semua',
    filterTonight: 'Malam ini',
    filterWeekend: 'Hujung minggu',
    errors: {
      shift_full: 'Kekosongan terakhir baru sahaja diambil orang.',
      tier_too_low: 'Anda perlu tier lebih tinggi untuk shift ini. Lihat Belajar untuk membukanya.',
      time_conflict: 'Ini bertindih dengan shift yang anda dah ambil.',
    },
  },
  myShifts: {
    title: 'Shift saya',
    upcoming: 'Akan datang',
    past: 'Lepas',
    emptyUpcoming: 'Tiada shift akan datang',
    emptyUpcomingBody: 'Lihat shift kosong dan ambil satu.',
    emptyPast: 'Belum ada shift lepas',
    browse: 'Lihat shift',
    clockIn: 'Daftar masuk',
    clockOut: 'Daftar keluar',
    clockedInAt: 'Daftar masuk pada {time}',
    onBreak: 'Sedang rehat',
    startBreak: 'Mula rehat',
    endBreak: 'Tamat rehat',
    cancelShift: 'Batalkan shift ini',
    cancelConfirm: 'Batalkan shift ini?',
    cancelBody: 'Membatalkan hampir dengan waktu mula akan menjejaskan skor kebolehpercayaan anda dan boleh menyebabkan akses shift dihentikan sementara.',
    cancelKeep: 'Kekalkan',
    cancelYes: 'Ya, batalkan',
    cancelled: 'Shift dibatalkan',
    completed: 'Selesai',
    noShow: 'Tidak hadir',
    worked: 'Bekerja {hours} jam',
  },
  clock: {
    title: 'Daftar masuk',
    titleOut: 'Daftar keluar',
    scanPrompt: 'Imbas kod QR di {site}',
    scanBody: 'Tanya pengurus bertugas di mana kod Pinch Crew dipaparkan.',
    enterCode: 'Daftar masuk',
    manualLabel: 'Kod daftar masuk',
    manualHint: 'Taip kod yang dipaparkan pada skrin Pinch Crew di lokasi anda.',
    cameraFailed: 'Kami tidak dapat membuka kamera. Taip kod sebagai ganti.',
    scan: 'Imbas kod QR',
    success: 'Anda telah daftar masuk',
    successBody: 'Selamat bekerja.',
    successOut: 'Anda telah daftar keluar',
    successOutBody: 'Jam kerja anda dihantar untuk kelulusan. Bayaran mengikut pusingan pembayaran seterusnya.',
    late: 'Anda daftar masuk lewat {n} minit',
    errors: {
      invalid_qr: 'Kod QR itu bukan kod daftar masuk Pinch Crew.',
      wrong_site: 'Kod ini untuk tempat kerja lain.',
      already_clocked_in: 'Anda sudah daftar masuk.',
      not_clocked_in: 'Anda belum daftar masuk.',
    },
  },
  earnings: {
    title: 'Pendapatan',
    pendingApproval: 'Menunggu kelulusan',
    pendingBody: 'Majikan ada {n} jam untuk semak jam kerja anda. Selepas itu ia lulus secara automatik.',
    nextPayout: 'Bayaran seterusnya',
    paidOut: 'Telah dibayar',
    thisMonth: 'Bulan ini',
    allTime: 'Keseluruhan',
    timesheets: 'Shift terkini',
    payouts: 'Sejarah bayaran',
    tsStatus: { pending: 'Menunggu kelulusan', approved: 'Diluluskan', disputed: 'Dipertikai', settled: 'Dibayar' },
    poStatus: { pending: 'Dijadualkan', processing: 'Diproses', paid: 'Dibayar', failed: 'Gagal' },
    autoApproves: 'Lulus automatik {when}',
    dispute: 'Jam kerja ini nampak salah',
    disputeTitle: 'Laporkan masalah',
    disputeBody: 'Beritahu kami jam kerja yang betul. Kami akan semak dengan majikan.',
    disputeReason: 'Apa yang berlaku?',
    disputeHours: 'Jam yang anda betul-betul bekerja',
    disputeSubmit: 'Hantar laporan',
    disputeSent: 'Laporan dihantar',
    empty: 'Belum ada pendapatan',
    emptyBody: 'Selesaikan shift pertama anda dan ia akan muncul di sini.',
  },
  learn: {
    title: 'Belajar',
    subtitle: 'Habiskan kursus, naikkan tier anda, dapat gaji lebih tinggi sejam.',
    unlockRate: 'Buka +RM{amount}/jam',
    startCourse: 'Mula kursus',
    continueCourse: 'Sambung',
    reviewCourse: 'Ulang kaji',
    completed: 'Selesai',
    duration: '{n} minit',
    modules: 'Modul',
    moduleDone: 'Selesai',
    markDone: 'Tanda selesai',
    quiz: 'Ujian pengetahuan',
    quizIntro: 'Jawab sekurang-kurangnya 80% betul untuk dapat sijil.',
    startQuiz: 'Mula ujian',
    submitQuiz: 'Hantar jawapan',
    quizPassed: 'Anda lulus',
    quizPassedBody: 'Markah {score}%. Tier {skill} anda kini T{tier} — anda boleh ambil shift bergaji lebih tinggi.',
    quizFailed: 'Belum cukup',
    quizFailedBody: 'Markah {score}%. Ulang kaji modul dan cuba lagi — tiada had percubaan.',
    tryAgain: 'Cuba lagi',
    certificates: 'Sijil anda',
    certIssued: 'Dikeluarkan {date}',
    currentTier: 'Tier anda: T{n}',
  },
  referrals: {
    title: 'Jemput kawan',
    subtitle: 'Krew yang bagus kenal krew yang bagus. Anda berdua dapat bayaran bila mereka habis shift pertama.',
    yourCode: 'Kod anda',
    share: 'Kongsi',
    copied: 'Disalin',
    youGet: 'Anda dapat RM{amount}',
    theyGet: 'Mereka dapat RM{amount}',
    invited: '{n} dijemput',
    waiting: '{n} menunggu shift pertama',
    earned: 'RM{amount} diperoleh',
    haveCode: 'Ada kod daripada kawan?',
    enterCode: 'Masukkan kod mereka',
    applyCode: 'Guna',
    codeApplied: 'Kod digunakan',
  },
  profile: {
    title: 'Profil',
    reliability: 'Kebolehpercayaan',
    reliabilityBody: 'Berdasarkan shift yang disiapkan, ketepatan masa dan pembatalan.',
    skills: 'Kemahiran anda',
    tierLabel: 'Tier {n}',
    ratings: 'Kata majikan',
    noRatings: 'Belum ada penilaian',
    homeArea: 'Di mana anda tinggal?',
    homeAreaHelp: 'Kod pos anda. Kami guna untuk tunjuk shift berdekatan rumah.',
    travelNeedsHome: 'Tambah kod pos di atas supaya kami boleh tapis shift ikut jarak.',
    travelRadius: 'Sejauh mana anda sanggup pergi?',
    travelValue: '{n} km',
    availability: 'Bila anda boleh bekerja?',
    documents: 'Dokumen',
    language: 'Bahasa',
    signOut: 'Log keluar',
    memberSince: 'Di Pinch Crew sejak {date}',
  },
}
